/**
 * Composant - HistoryPage
 *
 * Historique des opérations de caisse du caissier connecté (source : CBS).
 * - indicateurs sur la sélection (versements, retraits, flux net, nombre d'opérations) ;
 * - filtres : période, type, recherche (compte, client, référence, motif) ;
 * - tableau groupé par jour avec sous-totaux, pagination ;
 * - fiche d'une opération et réimpression du reçu (duplicata) ;
 * - export CSV et impression de la liste.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  History,
  Inbox,
  Printer,
  RefreshCw,
  Scale,
  Search,
  X,
} from 'lucide-react';
import { useOperationHistory, type HistoryTransaction } from '../../hooks/useOperationHistory';
import { useAuth } from '../../contexts/AuthContext';
import { imprimerRecu } from '../common/recuImpression';
import { montantEnLettres } from '../../../shared/utils/montantEnLettres';
import logoUnacoopec from '../../assets/logo-unacoopec.jpg';
import {
  type Periode,
  type PeriodeRapide,
  type TypeFiltre,
  dateCourte,
  echapperHtml,
  exporterOperationsCsv,
  fcfa,
  filtrer,
  imprimerHtml,
  iso,
  libelleJour,
  libellePeriode,
  nombre,
  parJour,
  periodeRapide,
  totaux,
} from './analyseOperations';
import './pages.css';

const PERIODES: { code: PeriodeRapide; libelle: string }[] = [
  { code: 'jour', libelle: "Aujourd'hui" },
  { code: 'hier', libelle: 'Hier' },
  { code: 'semaine', libelle: 'Cette semaine' },
  { code: 'mois', libelle: 'Ce mois' },
  { code: '30j', libelle: '30 jours' },
  { code: 'tout', libelle: 'Tout' },
];

const TAILLES = [15, 30, 50];

export function HistoryPage() {
  const { transactions, loading, error, refresh } = useOperationHistory();
  const { user } = useAuth();
  const nomCaissier = user?.displayName || user?.username || '';

  const [codePeriode, setCodePeriode] = useState<PeriodeRapide | 'perso'>('30j');
  const [periode, setPeriode] = useState<Periode>(periodeRapide('30j'));
  const [type, setType] = useState<TypeFiltre>('tous');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [taille, setTaille] = useState(15);
  const [selection, setSelection] = useState<HistoryTransaction | null>(null);

  const filtrees = useMemo(() => filtrer(transactions, { periode, type, recherche }), [transactions, periode, type, recherche]);
  const kpi = useMemo(() => totaux(filtrees), [filtrees]);
  const resumeJours = useMemo(() => new Map(parJour(filtrees).map((j) => [j.jour, j])), [filtrees]);

  useEffect(() => setPage(1), [periode, type, recherche, taille]);

  const nbPages = Math.max(1, Math.ceil(filtrees.length / taille));
  const pageCourante = Math.min(page, nbPages);
  const visibles = filtrees.slice((pageCourante - 1) * taille, pageCourante * taille);

  const choisirPeriode = (code: PeriodeRapide) => {
    setCodePeriode(code);
    setPeriode(periodeRapide(code));
  };
  const majDate = (champ: keyof Periode, valeur: string) => {
    setCodePeriode('perso');
    setPeriode((p) => ({ ...p, [champ]: valeur }));
  };
  const filtresActifs = type !== 'tous' || !!recherche || codePeriode !== '30j';
  const effacer = () => {
    choisirPeriode('30j');
    setType('tous');
    setRecherche('');
  };

  const exporter = () => exporterOperationsCsv(filtrees, `historique-operations-${iso(new Date())}.csv`);
  const imprimerListe = () => imprimerHtml(htmlListe(filtrees, libellePeriode(periode), nomCaissier));

  return (
    <div className="pg">
      <header className="pg-entete">
        <div className="titre">
          <span className="ico">
            <History />
          </span>
          <div>
            <h1>Historique des opérations</h1>
            <p>Vos versements et retraits enregistrés au CBS</p>
          </div>
        </div>
        <div className="pg-actions">
          <button type="button" className="pg-btn" onClick={() => refresh()} disabled={loading}>
            <RefreshCw className={loading ? 'pg-tourne' : undefined} /> Actualiser
          </button>
          <button type="button" className="pg-btn" onClick={imprimerListe} disabled={loading || filtrees.length === 0}>
            <Printer /> Imprimer
          </button>
          <button type="button" className="pg-btn principal" onClick={exporter} disabled={loading || filtrees.length === 0}>
            <Download /> Exporter (CSV)
          </button>
        </div>
      </header>

      {/* Indicateurs */}
      <section className="pg-kpis" aria-label="Indicateurs de la sélection">
        <Kpi ton="vert" icone={<ArrowDownLeft />} titre="Versements" valeur={fcfa(kpi.montantVersements)} sous={`${nombre(kpi.nbVersements)} opération(s)`} chargement={loading} />
        <Kpi ton="rose" icone={<ArrowUpRight />} titre="Retraits" valeur={fcfa(kpi.montantRetraits)} sous={`${nombre(kpi.nbRetraits)} opération(s)`} chargement={loading} />
        <Kpi
          ton="bleu"
          icone={<Scale />}
          titre="Flux net"
          valeur={`${kpi.net > 0 ? '+' : ''}${fcfa(kpi.net)}`}
          classeValeur={kpi.net >= 0 ? 'pg-pos' : 'pg-neg'}
          sous="Versements − retraits"
          chargement={loading}
        />
        <Kpi ton="gris" icone={<History />} titre="Opérations" valeur={nombre(kpi.nbOperations)} sous={libellePeriode(periode)} chargement={loading} />
      </section>

      {/* Filtres */}
      <section className="pg-filtres" aria-label="Filtres">
        <div className="pg-ligne">
          <div className="pg-segments" role="group" aria-label="Période">
            {PERIODES.map((p) => (
              <button key={p.code} type="button" className={codePeriode === p.code ? 'actif' : ''} onClick={() => choisirPeriode(p.code)}>
                {p.libelle}
              </button>
            ))}
          </div>
          <div className="pg-dates">
            <label htmlFor="hist-du">Du</label>
            <input id="hist-du" type="date" className="pg-input" value={periode.du} max={periode.au || undefined} onChange={(e) => majDate('du', e.target.value)} />
            <label htmlFor="hist-au">au</label>
            <input id="hist-au" type="date" className="pg-input" value={periode.au} min={periode.du || undefined} onChange={(e) => majDate('au', e.target.value)} />
          </div>
        </div>
        <div className="pg-ligne">
          <div className="pg-segments" role="group" aria-label="Type d'opération">
            {(['tous', 'versement', 'retrait'] as TypeFiltre[]).map((t) => (
              <button key={t} type="button" className={type === t ? 'actif' : ''} onClick={() => setType(t)}>
                {t === 'tous' ? 'Toutes' : t === 'versement' ? 'Versements' : 'Retraits'}
              </button>
            ))}
          </div>
          <div className="pg-recherche">
            <Search />
            <input
              type="search"
              className="pg-input"
              placeholder="Compte, client, référence ou motif…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              aria-label="Rechercher une opération"
            />
          </div>
          {filtresActifs && (
            <button type="button" className="pg-effacer" onClick={effacer}>
              <X /> Effacer les filtres
            </button>
          )}
        </div>
      </section>

      {error && (
        <div className="pg-erreur" role="alert">
          <AlertCircle />
          <div>
            <strong>L&apos;historique n&apos;a pas pu être chargé.</strong> {error}
          </div>
        </div>
      )}

      {/* Tableau */}
      <section className="pg-carte">
        <div className="pg-carte-tete">
          <h2>Opérations</h2>
          <span>
            {loading ? 'Chargement…' : `${nombre(filtrees.length)} opération${filtrees.length > 1 ? 's' : ''} · ${libellePeriode(periode)}`}
          </span>
        </div>

        <div className="pg-table-wrap">
          <table className="pg-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Client</th>
                <th className="masquer-mobile">Motif</th>
                <th className="masquer-mobile">Référence</th>
                <th className="droite">Montant</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6}>
                      <div className="pg-squelette" style={{ width: `${60 + ((i * 7) % 35)}%` }} />
                    </td>
                  </tr>
                ))}

              {!loading && visibles.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="pg-vide">
                      <Inbox />
                      <strong>Aucune opération</strong>
                      <p>{transactions.length === 0 ? "Aucune opération n'a encore été enregistrée." : 'Aucune opération ne correspond aux filtres.'}</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                visibles.map((t, i) => {
                  const jour = iso(t.date);
                  const nouveauJour = i === 0 || iso(visibles[i - 1].date) !== jour;
                  const r = resumeJours.get(jour);
                  return (
                    <FragmentJour key={t.id + i} afficher={nouveauJour} jour={jour} resume={r}>
                      <tr
                        className="op"
                        tabIndex={0}
                        onClick={() => setSelection(t)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelection(t)}
                      >
                        <td>
                          <TypeOp type={t.type} />
                        </td>
                        <td>
                          <div className="pg-client">
                            <div className="nom">{t.clientName || 'Client'}</div>
                            <div className="cpt">{t.accountNumber}</div>
                          </div>
                        </td>
                        <td className="masquer-mobile">
                          <div className="pg-motif" title={t.motif}>
                            {t.motif || '—'}
                          </div>
                        </td>
                        <td className="masquer-mobile">
                          <span className="pg-ref">{t.reference}</span>
                        </td>
                        <td className="droite">
                          <span className={`pg-montant ${t.type === 'versement' ? 'pg-pos' : 'pg-neg'}`}>
                            {t.type === 'versement' ? '+' : '−'}
                            {fcfa(t.amount)}
                          </span>
                        </td>
                        <td className="droite">
                          <button
                            type="button"
                            className="pg-btn icone"
                            aria-label="Voir le détail"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelection(t);
                            }}
                          >
                            <Eye />
                          </button>
                        </td>
                      </tr>
                    </FragmentJour>
                  );
                })}
            </tbody>
          </table>
        </div>

        {!loading && filtrees.length > 0 && (
          <div className="pg-pied">
            <div className="pg-dates">
              <span>
                {(pageCourante - 1) * taille + 1}–{Math.min(pageCourante * taille, filtrees.length)} sur {nombre(filtrees.length)}
              </span>
              <select className="pg-input" value={taille} onChange={(e) => setTaille(Number(e.target.value))} aria-label="Lignes par page">
                {TAILLES.map((t) => (
                  <option key={t} value={t}>
                    {t} par page
                  </option>
                ))}
              </select>
            </div>
            <nav className="pg-pages" aria-label="Pagination">
              <button type="button" onClick={() => setPage(pageCourante - 1)} disabled={pageCourante <= 1} aria-label="Page précédente">
                <ChevronLeft />
              </button>
              {numerosPages(pageCourante, nbPages).map((n, i) =>
                n === 0 ? (
                  <span key={`e${i}`} style={{ padding: '0 4px' }}>
                    …
                  </span>
                ) : (
                  <button key={n} type="button" className={n === pageCourante ? 'actif' : ''} onClick={() => setPage(n)} aria-current={n === pageCourante ? 'page' : undefined}>
                    {n}
                  </button>
                ),
              )}
              <button type="button" onClick={() => setPage(pageCourante + 1)} disabled={pageCourante >= nbPages} aria-label="Page suivante">
                <ChevronRight />
              </button>
            </nav>
          </div>
        )}
      </section>

      <p className="pg-note">Le CBS fournit la date de chaque opération, sans l&apos;heure.</p>

      {selection && (
        <DetailOperation
          operation={selection}
          nomCaissier={nomCaissier}
          onFermer={() => setSelection(null)}
        />
      )}
    </div>
  );
}

// ─── Sous-composants ────────────────────────────────────────────────────────

function Kpi({
  ton,
  icone,
  titre,
  valeur,
  sous,
  classeValeur,
  chargement,
}: {
  ton: 'vert' | 'rose' | 'bleu' | 'gris';
  icone: React.ReactNode;
  titre: string;
  valeur: string;
  sous: string;
  classeValeur?: string;
  chargement?: boolean;
}) {
  return (
    <div className={`pg-kpi ${ton}`}>
      <div className="haut">
        <span>{titre}</span>
        <span className="pastille">{icone}</span>
      </div>
      {chargement ? <div className="pg-squelette" style={{ height: 24, width: '70%' }} /> : <div className={`val ${classeValeur ?? ''}`}>{valeur}</div>}
      <div className="sous">{sous}</div>
    </div>
  );
}

function TypeOp({ type }: { type: 'versement' | 'retrait' }) {
  return (
    <span className={`pg-type ${type}`}>
      {type === 'versement' ? <ArrowDownLeft /> : <ArrowUpRight />}
      {type === 'versement' ? 'Versement' : 'Retrait'}
    </span>
  );
}

function FragmentJour({
  afficher,
  jour,
  resume,
  children,
}: {
  afficher: boolean;
  jour: string;
  resume?: { nbVersements: number; nbRetraits: number; montantVersements: number; montantRetraits: number };
  children: React.ReactNode;
}) {
  return (
    <>
      {afficher && (
        <tr className="jour">
          <td colSpan={6}>
            {libelleJour(jour)}
            {resume && (
              <span className="resume">
                <span className="pg-pos">+{fcfa(resume.montantVersements)}</span> · <span className="pg-neg">−{fcfa(resume.montantRetraits)}</span> ·{' '}
                {resume.nbVersements + resume.nbRetraits} op.
              </span>
            )}
          </td>
        </tr>
      )}
      {children}
    </>
  );
}

function DetailOperation({ operation: t, nomCaissier, onFermer }: { operation: HistoryTransaction; nomCaissier: string; onFermer: () => void }) {
  useEffect(() => {
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && onFermer();
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [onFermer]);

  const versement = t.type === 'versement';
  const reimprimer = () =>
    imprimerRecu({
      id: t.id,
      type: versement ? 'DEPOSIT' : 'WITHDRAWAL',
      amount: t.amount,
      currency: t.currency || 'XOF',
      accountNumber: t.accountNumber,
      clientId: '',
      clientName: t.clientName || 'Client',
      cashierId: t.cashier,
      cashierName: nomCaissier || t.cashier,
      createdAt: t.date.toISOString(),
      reference: t.reference,
      biotransacRef: t.reference,
      status: 'COMPLETED',
      destination: versement ? undefined : t.motif,
      duplicata: true,
    });

  const lignes: [string, React.ReactNode][] = [
    ['Date', dateCourte(t.date)],
    ['Référence', <span key="ref" className="pg-ref">{t.reference || '—'}</span>],
    ['Compte', <span key="cpt" className="pg-ref">{t.accountNumber || '—'}</span>],
    ['Titulaire', t.clientName || '—'],
    ['Motif', t.motif || '—'],
    ...(t.isTiers && t.deposantNom ? [['Déposant', t.deposantNom] as [string, React.ReactNode]] : []),
    ['Caissier', nomCaissier ? `${nomCaissier} (${t.cashier})` : t.cashier],
    ['Statut', t.status === 'completed' ? 'Exécutée' : t.status === 'pending' ? 'En attente' : 'Annulée'],
  ];

  return (
    <div className="pg-voile" onClick={onFermer} role="presentation">
      <aside className="pg-panneau" role="dialog" aria-modal="true" aria-labelledby="titre-detail" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2 id="titre-detail">Détail de l&apos;opération</h2>
          <button type="button" className="pg-btn icone" onClick={onFermer} aria-label="Fermer">
            <X />
          </button>
        </header>
        <div className="contenu">
          <div>
            <TypeOp type={t.type} />
          </div>
          <div className="montant">
            <div className={`val ${versement ? 'pg-pos' : 'pg-neg'}`}>
              {versement ? '+' : '−'}
              {fcfa(t.amount)}
            </div>
            <div className="lettres">{montantEnLettres(t.amount)}</div>
          </div>
          <dl>
            {lignes.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <footer>
          <button type="button" className="pg-btn" onClick={onFermer}>
            Fermer
          </button>
          <button type="button" className="pg-btn principal" onClick={reimprimer}>
            <Printer /> Duplicata du reçu
          </button>
        </footer>
      </aside>
    </div>
  );
}

function numerosPages(courante: number, total: number): number[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, courante - 1, courante, courante + 1]);
  const liste = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const resultat: number[] = [];
  liste.forEach((n, i) => {
    if (i > 0 && n - liste[i - 1] > 1) resultat.push(0);
    resultat.push(n);
  });
  return resultat;
}

/** Liste imprimable (A4 paysage) des opérations filtrées */
function htmlListe(ops: HistoryTransaction[], periode: string, caissier: string): string {
  const k = totaux(ops);
  const logo = new URL(logoUnacoopec, window.location.href).href;
  const lignes = ops
    .map(
      (t) => `<tr>
        <td>${dateCourte(t.date)}</td>
        <td>${t.type === 'versement' ? 'Versement' : 'Retrait'}</td>
        <td>${echapperHtml(t.clientName)}<br><small>${echapperHtml(t.accountNumber)}</small></td>
        <td>${echapperHtml(t.motif)}</td>
        <td class="mono">${echapperHtml(t.reference)}</td>
        <td class="d ${t.type === 'versement' ? 'pos' : 'neg'}">${t.type === 'versement' ? '+' : '−'}${fcfa(t.amount)}</td>
      </tr>`,
    )
    .join('');
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Historique des opérations</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #111827; font-size: 9.5pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  header { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0B677E; padding-bottom: 8px; margin-bottom: 10px; }
  header img { height: 36px; } header .t { flex: 1; } header h1 { margin: 0; font-size: 14pt; color: #0B677E; }
  header p { margin: 2px 0 0; color: #6b7280; } .resume { display: flex; gap: 18px; margin-bottom: 10px; }
  .resume div { border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 10px; } .resume b { display: block; font-size: 11pt; }
  table { width: 100%; border-collapse: collapse; } th, td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 8pt; text-transform: uppercase; color: #4b5563; } small { color: #6b7280; }
  .d { text-align: right; white-space: nowrap; font-weight: 700; } .pos { color: #016951; } .neg { color: #e11d48; } .mono { font-family: Consolas, monospace; font-size: 8pt; }
  footer { margin-top: 8px; color: #6b7280; font-size: 8pt; }
</style></head><body>
<header><img src="${echapperHtml(logo)}" alt=""><div class="t"><h1>Historique des opérations de caisse</h1>
<p>${echapperHtml(periode)}${caissier ? ` · Caissier : ${echapperHtml(caissier)}` : ''}</p></div>
<div>Édité le ${dateCourte(new Date())}</div></header>
<div class="resume">
  <div>Versements<b class="pos">${fcfa(k.montantVersements)}</b>${k.nbVersements} op.</div>
  <div>Retraits<b class="neg">${fcfa(k.montantRetraits)}</b>${k.nbRetraits} op.</div>
  <div>Flux net<b>${fcfa(k.net)}</b>${k.nbOperations} op. au total</div>
</div>
<table><thead><tr><th>Date</th><th>Type</th><th>Client / compte</th><th>Motif</th><th>Référence</th><th class="d">Montant</th></tr></thead>
<tbody>${lignes}</tbody></table>
<footer>Source : historique CBS — BIOTRANSAC, UNACOOPEC.</footer>
</body></html>`;
}
